import { toInventoryList } from '../api/inventory-request';
import { normalizeControlledAuthStatus } from './controlledDrug';
import {
  hasControlledDataSignal,
  isActivePrescriptionStatus,
  normalizePrescriptionStatus,
} from './prescriptionStatus';
import { lineAcceptedQuantity, summarizeFulfilment } from './prescriptionFulfilment';
import {
  explicitPrescribedQty,
  medicineDetails,
  resolveDoseText,
  resolveDurationText,
  resolveInstructions,
  resolvePrescribedQty,
  resolveRoute,
} from './prescriptionMedicine';

// Prescription status logic lives in the import-free ./prescriptionStatus so it can
// be unit-tested (this module reaches axios through api/inventory-request). Re-exported
// here so existing importers keep working unchanged.
export { normalizePrescriptionStatus, isActivePrescriptionStatus };

function pick(row, ...keys) {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    const value = row[key];
    if (value != null && value !== '') return value;
  }
  return '';
}

function pickNum(row, ...keys) {
  const value = pick(row, ...keys);
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function pickBool(row, ...keys) {
  for (const key of keys) {
    if (row?.[key] != null) return Boolean(row[key]);
  }
  return false;
}

/** Like pickNum, but preserves "missing" as null instead of defaulting to 0 —
 *  needed for gst_rate, where 0 (0% GST) and "not returned yet" must stay distinguishable. */
function pickNumOrNull(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== '') {
      const num = Number(value);
      if (Number.isFinite(num)) return num;
    }
  }
  return null;
}

function prescriptionMedicines(row) {
  if (!row || typeof row !== 'object') return [];
  if (Array.isArray(row.medicines) && row.medicines.length) return row.medicines;
  if (Array.isArray(row.lines) && row.lines.length) return row.lines;
  return [];
}

function patientClinicalAlertArrays(row) {
  if (!row || typeof row !== 'object') return [];
  for (const key of ['allergies', 'patient_clinical_alerts', 'clinical_alerts', 'patient_allergies']) {
    if (Array.isArray(row[key]) && row[key].length) return row[key];
  }
  return [];
}

function isPatientClinicalAlertType(type) {
  const normalized = String(type || '').toUpperCase();
  if (!normalized) return false;
  if (
    normalized.includes('ALLERGY')
    || normalized.includes('CONTRAINDICATION')
    || normalized.includes('COMORBID')
    || ['MEDICAL_CONDITION', 'PREGNANCY', 'BREASTFEEDING', 'ADVERSE_REACTION'].includes(normalized)
  ) {
    return true;
  }
  return !['STOCK', 'CONTROLLED', 'INTERACTION', 'DUPLICATE', 'DOSE_RANGE', 'CLINICAL_REVIEW'].some(
    (token) => normalized.includes(token),
  ) && normalized.includes('PATIENT');
}

export function mapPatientClinicalAlert(row, id) {
  // IPD registration captures allergies as one free-text field, so the doctor console
  // sends `allergies` as a flat string[] (see OrderManagementDrawer's [PHARMACY_RX]
  // note). `pick()` only reads objects, so those rows used to render as the literal
  // word "ALLERGY" with the allergen text dropped — lift the string into `allergen`.
  if (typeof row === 'string') {
    const text = row.trim();
    return mapPatientClinicalAlert(text ? { allergen: text } : {}, id);
  }

  const type = pick(row, 'type') || 'ALLERGY';
  const allergen = pick(row, 'allergen', 'name', 'description', 'allergy_description', 'title');
  const severity = String(pick(row, 'severity') || 'MODERATE').toUpperCase();
  const reaction = pick(row, 'reaction', 'note', 'clinical_status_description');
  const message = pick(row, 'message') || (allergen && reaction ? `${allergen}: ${reaction}` : allergen || reaction);
  return {
    id: id || pick(row, 'id') || `${type}-${allergen || message}`,
    type,
    allergen: allergen || String(type).replace(/_/g, ' '),
    severity,
    reaction,
    message,
    verified: pickBool(row, 'verified'),
    _raw: row,
  };
}

export function extractPatientClinicalAlerts(prescriptionPayload) {
  const raw = prescriptionPayload?._raw || prescriptionPayload;
  const fromRoot = patientClinicalAlertArrays(raw).map((row, index) => mapPatientClinicalAlert(row, `root-${index}`));
  if (fromRoot.length) return fromRoot;

  const fromLines = [];
  prescriptionMedicines(raw).forEach((line, lineIndex) => {
    const lineId = pick(line, 'id', 'item_id') || `line-${lineIndex}`;
    (line.alerts || []).forEach((alert, alertIndex) => {
      if (!isPatientClinicalAlertType(alert.type)) return;
      fromLines.push(mapPatientClinicalAlert({
        ...alert,
        allergen: alert.allergen || String(alert.message || '').split(':')[0]?.trim(),
        reaction: alert.reaction || String(alert.message || '').split(':').slice(1).join(':').trim(),
      }, `${lineId}-${alertIndex}`));
    });
  });
  return fromLines;
}

function countPatientClinicalAlerts(row) {
  return extractPatientClinicalAlerts(row).length;
}

function formatWardLocation(row) {
  const room = pick(row, 'room_no', 'ward', 'ward_name');
  const bed = pick(row, 'bed_no');
  if (room && bed) return `${room} · Bed ${bed}`;
  return room || bed || '';
}

export function mapPrescription(row, index = 0) {
  const medicines = prescriptionMedicines(row);
  const primaryId = pick(row, 'id', 'prescription_id', 'order_no', 'prescription_number', 'order_number');
  const id = primaryId || [
    pick(row, 'admission_id'),
    pick(row, 'uhid', 'patient_id'),
    pick(row, 'created_at', 'updated_at'),
    String(index),
  ].filter(Boolean).join('|') || `rx-${index}`;
  const alertCount = countPatientClinicalAlerts(row);
  const normalizedStatus = normalizePrescriptionStatus(pick(row, 'status'));
  return {
    id,
    tenant_id: pick(row, 'tenant_id'),
    hospital_id: pick(row, 'hospital_id'),
    order_number: pick(row, 'prescription_number', 'order_number', 'transaction_number', 'uhid'),
    patient_id: pick(row, 'patient_id', 'uhid', 'external_patient_id'),
    patient_name: pick(row, 'name', 'patient_name', 'patient_id'),
    doctor_name: pick(row, 'doctor_name', 'doctor_id'),
    doctor_id: pick(row, 'doctor_id'),
    ward: formatWardLocation(row) || pick(row, 'ward', 'ward_name', 'ward_id'),
    bed_no: pick(row, 'bed_no'),
    room_no: pick(row, 'room_no'),
    admission_id: pick(row, 'admission_id'),
    billing_account_id: pick(row, 'billing_account_id', 'billing_account'),
    uhid: pick(row, 'uhid'),
    phone_no: pick(row, 'phone_no', 'phone'),
    provisional_diagnosis: pick(row, 'provisional_diagnosis'),
    final_diagnosis: pick(row, 'final_diagnosis'),
    created_at: pick(row, 'created_at', 'order_date', 'prescription_date', 'created_date', 'date'),
    updated_at: pick(row, 'updated_at'),
    // No `received_at`: /get_ipd_prescriptions/ has no pharmacy-receipt timestamp, and
    // the old `|| updated_at` fallback made every untouched order render a "Received"
    // time identical to its "Ordered" time. Restore it only when the API sends one.
    sent_at: pick(row, 'sent_at', 'pharmacy_received_at'),
    date: pick(row, 'created_at', 'updated_at', 'order_date', 'created_date', 'date', 'transaction_date', 'prescription_date'),
    status: normalizedStatus,
    status_label: pick(row, 'status') || normalizedStatus,
    priority: pick(row, 'priority') || 'ROUTINE',
    item_count: medicines.length || pickNum(row, 'item_count', 'items_count', 'line_count', 'total_items'),
    has_controlled: pickBool(row, 'has_controlled') || pickNum(row, 'controlled_count') > 0
      || medicines.some((line) => pickBool(line, 'controlled', 'is_controlled_drug', 'is_controlled')),
    // Whether the payload CARRIES controlled-drug data at all, independent of the
    // answer. `has_controlled: false` means "none are controlled" (a real 0);
    // no key at all means "the API does not tell us", which must not be shown as 0.
    controlled_data_available: hasControlledDataSignal(row, medicines),
    has_stat: medicines.some((line) => pickBool(line, 'stat') || pickBool(line?.details, 'stat')),
    has_alerts: alertCount > 0 || pickBool(row, 'has_alerts') || pickNum(row, 'alert_count') > 0,
    alert_count: alertCount || pickNum(row, 'alert_count'),
    // Ordered/delivered/accepted rollup for the queue's progress labels and the
    // "delivered, awaiting ward confirm" signal. Derived here once so the table
    // never re-walks medicines[] per render.
    fulfilment: summarizeFulfilment(medicines),
    _raw: row,
  };
}

export function mapPrescriptionItem(row, index = 0, prescriptionId = '') {
  const details = medicineDetails(row);
  const medicineName = pick(row, 'medicine_name', 'name', 'instructions', 'medicine', 'item_name');
  // CPOE rows keep the generic under details.generic_name; older rows on the row itself.
  const genericName = pick(row, 'generic_name', 'generic', 'salt_name', 'generic_medicine_name', 'generic_medicine_names')
    || pick(details, 'generic_name');
  const lineId = pick(row, 'id', 'line_id', 'prescription_item_id', 'item_id')
    || `${prescriptionId || 'rx'}-med-${index}`;
  return {
    id: lineId,
    medicine: medicineName,
    generic_name: genericName,
    dosage: resolveDoseText(row),
    frequency: pick(row, 'frequency_code', 'frequency') || pick(details, 'frequency_code', 'frequency'),
    duration: resolveDurationText(row),
    prescribed_qty: resolvePrescribedQty(row),
    // Whether `prescribed_qty` is a real prescribed number or this module's estimate.
    // The dispense screen must not offer an estimate as if a doctor had written it.
    qty_specified: explicitPrescribedQty(row) > 0,
    // How much has already gone out, so `prescribed_qty - this` is what is
    // genuinely still pending. `delivered_quantity` leads deliberately:
    // `accepted_quantity` lags it until the ward confirms receipt, and
    // subtracting the ward's figure would offer units the pharmacy has already
    // handed over for dispense a second time.
    dispensed_qty: pickNum(row, 'delivered_quantity', 'issued_quantity', 'dispensed_qty', 'dispensed_quantity', 'issued_qty'),
    // The ward's confirmed figure, shown alongside delivered so the gap between
    // the two ("delivered, awaiting ward confirm") is visible per line. Read
    // through lineAcceptedQuantity because the COLUMN is dead and the live value
    // sits in details.accepted_quantity.
    accepted_qty: lineAcceptedQuantity(row),
    route: resolveRoute(row),
    line_status: pick(row, 'line_status', 'status') || 'PENDING',
    controlled: pickBool(row, 'controlled', 'is_controlled_drug', 'is_narcotic', 'is_controlled'),
    schedule: pick(row, 'schedule', 'drug_schedule'),
    batch_number: pick(row, 'batch_number'),
    unit_rate: pick(row, 'unit_rate', 'rate'),
    item_id: pick(row, 'brand_item_id', 'generic_item_id', 'item_id'),
    uom_id: resolveMedicineUomId(row, details),
    instructions: resolveInstructions(row),
    alerts: Array.isArray(row.alerts) ? row.alerts : [],
    _raw: row,
  };
}

export function mapPrescriptionLines(prescriptionPayload) {
  const raw = prescriptionPayload?._raw || prescriptionPayload;
  const prescriptionId = pick(raw, 'id', 'prescription_id', 'order_no', 'prescription_number', 'order_number');
  return prescriptionMedicines(raw).map((row, index) => mapPrescriptionItem(row, index, prescriptionId));
}

export function mapClinicalAlert(alert, id, line) {
  const type = pick(alert, 'type') || 'ALERT';
  return {
    id: id || pick(alert, 'id') || `${type}-${pick(line, 'id', 'item_id')}`,
    type,
    message: pick(alert, 'message'),
    line_id: pick(line, 'id', 'item_id'),
    medicine: pick(line, 'name', 'instructions', 'medicine', 'medicine_name', 'item_name', 'item_id'),
    severity: pick(alert, 'severity') || inferAlertSeverity(type),
    _raw: alert,
  };
}

function inferAlertSeverity(type) {
  const normalized = String(type || '').toUpperCase();
  if (normalized.includes('SEVERE') || normalized.includes('ALLERGY')) return 'SEVERE';
  if (normalized.includes('INTERACTION') || normalized.includes('DUPLICATE')) return 'MODERATE';
  if (normalized.includes('STOCK')) return 'MILD';
  return 'MODERATE';
}

export function extractClinicalAlertsFromPrescription(prescriptionPayload) {
  const raw = prescriptionPayload?._raw || prescriptionPayload;
  const alerts = [];

  prescriptionMedicines(raw).forEach((line, lineIndex) => {
    const lineId = pick(line, 'id', 'item_id') || `line-${lineIndex}`;
    (line.alerts || []).forEach((alert, alertIndex) => {
      alerts.push(mapClinicalAlert(alert, `${lineId}-${alertIndex}`, line));
    });
  });

  return alerts;
}

export function mapControlledItem(row) {
  return {
    id: pick(row, 'auth_id', 'id', 'queue_id', 'controlled_item_id'),
    prescription_id: pick(row, 'prescription_id'),
    order_number: pick(row, 'order_number', 'prescription_number', 'uhid'),
    transaction_id: pick(row, 'inventory_transaction_id', 'transaction_id'),
    transaction_item_id: pick(row, 'inventory_transaction_item_id', 'transaction_item_id'),
    item_id: pick(row, 'item_id'),
    patient_name: pick(row, 'patient_name', 'patient_id'),
    patient_mobile: pick(row, 'patient_mobile'),
    medicine: pick(row, 'medicine', 'medicine_name', 'item_name'),
    schedule: pick(row, 'schedule', 'drug_schedule'),
    qty: pickNum(row, 'qty', 'quantity'),
    prescriber: pick(row, 'prescriber', 'prescriber_name', 'doctor_name', 'doctor_id'),
    requested_by: pick(
      row,
      'requested_by_name',
      'requester_name',
      'created_by_name',
      'cashier_name',
      'requested_by',
    ),
    reason: pick(row, 'reason'),
    status: normalizeControlledAuthStatus(pick(row, 'status')),
    requested_at: pick(row, 'requested_at', 'created_at', 'request_date'),
    // Where this request came from. A counter sale resumes on the Direct
    // screen; a staged dispense on its own detail page — the queue must not
    // send every row to /dispenses/:id.
    source_type: pick(row, 'transaction_type'),
    source_number: pick(row, 'transaction_number'),
    // Status of the parent sale — an open-orders list uses it to drop the ones
    // already finalized or cancelled.
    source_status: pick(row, 'transaction_status'),
    resume_path: pick(row, 'resume_path'),
    approved_by: pick(row, 'supervisor_name', 'supervisor_id'),
    approved_at: pick(row, 'authorized_at'),
    _raw: row,
  };
}

/** Credit note / refund receipt number from pharmacy refund payloads. */
export function pharmacyRefundCnNo(row) {
  return pick(
    row,
    'receipt_number',
    'credit_note_number',
    'cn_number',
    'cn_no',
    'refund_number',
  ) || '—';
}

/** Amount still to settle when API omits remaining_amount. */
export function pharmacyRefundRemaining(row) {
  if (!row || typeof row !== 'object') return 0;
  if (row.remaining_amount != null && row.remaining_amount !== '') {
    return pickNum(row, 'remaining_amount');
  }
  const approved = pickNum(row, 'approved_amount');
  const settled = pickNum(row, 'settled_amount');
  const total = pickNum(row, 'total_amount', 'amount');
  if (approved > 0) return Math.max(0, approved - settled);
  return Math.max(0, total - settled);
}

/** Normalize global refunds queue + per-bill refund list rows. */
export function mapPharmacyRefund(row) {
  const billId = pick(row, 'pharmacy_bill_id', 'bill_id');
  const cn = pharmacyRefundCnNo(row);
  const refundItems = (row.refund_items || row.items || []).map((it) => ({
    ...it,
    item_name: pick(it, 'item_name', 'medicine_name', 'name', 'product_name') || '',
    batch_number: pick(it, 'batch_number', 'batch'),
    return_quantity: pick(it, 'return_quantity', 'quantity', 'qty'),
    refund_amount: pick(it, 'refund_amount', 'amount', 'line_amount'),
  }));
  return {
    ...row,
    id: pick(row, 'id', 'refund_id') || row.id,
    refund_id: pick(row, 'refund_id', 'id') || row.id,
    bill_id: billId,
    pharmacy_bill_id: billId,
    bill_number: pick(row, 'bill_number', 'pharmacy_bill_number') || billId,
    credit_note_number: cn === '—' ? '' : cn,
    total_amount: pick(row, 'total_amount', 'amount') || row.total_amount,
    remaining_amount: pharmacyRefundRemaining(row),
    raised_at: pick(row, 'created_at', 'raised_at', 'payment_date'),
    refund_items: refundItems,
    _raw: row,
  };
}

/** One-line label for a refund item: "Paracetamol 500mg × 2 · BATCH-001". */
export function pharmacyRefundItemLabel(item) {
  if (!item || typeof item !== 'object') return 'Item';
  const name = pick(item, 'item_name', 'medicine_name', 'name', 'product_name') || 'Item';
  const qty = pick(item, 'return_quantity', 'quantity', 'qty');
  const batch = pick(item, 'batch_number', 'batch');
  const parts = [name];
  if (qty !== '' && qty != null) parts.push(`× ${qty}`);
  let label = parts.join(' ');
  if (batch) label += ` · ${batch}`;
  return label;
}

/** Compact summary for table cells, e.g. "Paracetamol 500mg × 2 (+1)". */
export function pharmacyRefundItemsSummary(row) {
  const items = row?.refund_items || row?.items || [];
  if (!items.length) return '—';
  const first = pharmacyRefundItemLabel(items[0]);
  if (items.length === 1) return first;
  return `${first} (+${items.length - 1})`;
}

export function mapBill(row) {
  return {
    id: pick(row, 'id', 'bill_id', 'transaction_id'),
    bill_number: pick(row, 'bill_number', 'invoice_number', 'id', 'bill_id'),
    dispense_id: pick(row, 'dispense_id', 'transaction_id', 'dispense_number', 'sale_id'),
    patient_name: pick(row, 'search_name', 'patient_name') || 'Walk-in',
    search_mobile: pick(row, 'search_mobile', 'mobile', 'phone'),
    amount: pickNum(row, 'amount_payable', 'amount', 'total_value', 'total_amount'),
    collected: pickNum(row, 'amount_collected', 'collected_amount', 'paid_amount'),
    refunded: pickNum(row, 'amount_refunded', 'refunded_amount'),
    payment_mode: pick(row, 'payment_mode', 'payment_method'),
    date: pick(row, 'date', 'transaction_date', 'created_at', 'posted_at', 'bill_date'),
    status: pick(row, 'bill_status', 'status', 'billing_status') || 'DRAFT',
    tenant_id: pick(row, 'tenant_id'),
    hospital_id: pick(row, 'hospital_id'),
    _raw: row,
  };
}

export function mapPreAuth(row) {
  return {
    id: pick(row, 'id', 'pre_auth_id', 'request_id'),
    dispense_id: pick(row, 'dispense_id', 'transaction_id', 'dispense_number'),
    patient_name: pick(row, 'patient_name', 'patient_id'),
    insurer: pick(row, 'insurer', 'insurance_provider', 'payer_name'),
    amount: pickNum(row, 'amount', 'total_value', 'authorized_amount'),
    status: pick(row, 'status') || 'PENDING_INSURANCE',
    submitted_at: pick(row, 'submitted_at', 'created_at', 'request_date'),
    _raw: row,
  };
}

export function mapWardReturn(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: pick(row, 'id'),
    returnId: pick(row, 'transaction_number', 'id'),
    ward: pick(row, 'ward_id') || '—',
    patient_id: pick(row, 'patient_id') || '—',
    admission_id: pick(row, 'admission_id') || '—',
    date: pick(row, 'transaction_date', 'created_at'),
    status: pick(row, 'status') || 'SUBMITTED',
    remarks: pick(row, 'remarks'),
    to_store_id: pick(row, 'to_store_id'),
    billing_account_id: pick(row, 'billing_account_id'),
    itemCount: null,
    _raw: row,
  };
}

export function mapWardReturnItem(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: pick(row, 'id'),
    item_id: pick(row, 'item_id'),
    uom_id: pick(row, 'uom_id'),
    batch_number: pick(row, 'batch_number', 'batch') || '—',
    expiry_date: pick(row, 'expiry_date'),
    requested_quantity: Number(row.requested_quantity ?? 0),
    accepted_quantity: Number(row.accepted_quantity ?? 0),
    rejected_quantity: Number(row.rejected_quantity ?? 0),
    returned_quantity: Number(row.returned_quantity ?? 0),
    rejection_reason: pick(row, 'rejection_reason'),
    unit_rate: Number(row.unit_rate ?? 0),
    line_total: Number(row.line_total ?? 0),
    remarks: pick(row, 'remarks'),
    _raw: row,
  };
}

export function mapList(payload, mapper) {
  return toInventoryList(payload).map(mapper);
}

/** Prefer the batch with a real sale price, falling back to one with an MRP. */
function bestPricedBatch(batches) {
  if (!Array.isArray(batches) || !batches.length) return null;
  return batches.find((b) => Number(b.sale_price) > 0)
    || batches.find((b) => Number(b.mrp) > 0)
    || null;
}

function normalizeCatalogueBatch(batch) {
  if (!batch || typeof batch !== 'object') return batch;
  return {
    ...batch,
    batch_number: pick(batch, 'batch_number', 'batch_no', 'lot_number'),
    available_quantity: pick(batch, 'available_quantity', 'quantity', 'on_hand_quantity', 'stock'),
    sale_price: batch.sale_price ?? batch.unit_rate ?? batch.rate,
    mrp: batch.mrp,
    // The same two prices per the item's issue/dispense unit, straight from the
    // catalogue. A screen taking quantity in strips prices off these; it must
    // never derive them by multiplying the base price itself.
    sale_price_issue_uom: batch.sale_price_issue_uom,
    mrp_issue_uom: batch.mrp_issue_uom,
    // How many base units one issue unit is FOR THIS BATCH. A GRN line can
    // freeze a pack size that differs from the item master's, and old and new
    // pack sizes live in the ledger at the same time. Null falls back to the
    // item-level factor at the point of use.
    issue_uom_factor: pickNumOrNull(batch, 'issue_uom_factor'),
    expiry_date: batch.expiry_date,
    inventory_transaction_id: pick(
      batch,
      'inventory_transaction_id',
      'transaction_id',
      'inventory_txn_id',
    ),
    inventory_transaction_item_id: pick(
      batch,
      'inventory_transaction_item_id',
      'transaction_item_id',
      'inventory_txn_item_id',
      'stock_balance_id',
    ),
  };
}

/** Store-catalogue and prescribe/search may nest batches or flatten a single batch on the item row. */
export function normalizeCatalogueBatches(row) {
  if (!row || typeof row !== 'object') return [];
  const nested = [row.batches, row.batch_lines, row.stock_batches].find((arr) => Array.isArray(arr) && arr.length);
  if (nested) return nested.map(normalizeCatalogueBatch);
  const batchNo = pick(row, 'batch_number', 'batch_no', 'lot_number');
  if (!batchNo) return [];
  return [normalizeCatalogueBatch({
    batch_number: batchNo,
    expiry_date: pick(row, 'expiry_date'),
    available_quantity: pick(row, 'available_quantity', 'total_available_quantity', 'quantity', 'stock'),
    sale_price: pick(row, 'sale_price', 'unit_rate', 'rate'),
    mrp: pick(row, 'mrp'),
    sale_price_issue_uom: pick(row, 'sale_price_issue_uom'),
    mrp_issue_uom: pick(row, 'mrp_issue_uom'),
    issue_uom_factor: pickNumOrNull(row, 'issue_uom_factor'),
    grn_item_id: pick(row, 'grn_item_id'),
    stock_balance_id: pick(row, 'stock_balance_id'),
    inventory_transaction_id: pick(row, 'inventory_transaction_id', 'transaction_id'),
    inventory_transaction_item_id: pick(
      row,
      'inventory_transaction_item_id',
      'transaction_item_id',
      'stock_balance_id',
    ),
    fefo_rank: row.fefo_rank,
    is_expired: row.is_expired,
  })];
}

/**
 * Resolve a UOM UUID from prescribe/search, store-catalogue, or item-master shapes.
 * Search APIs often omit top-level uom_id; check nested base_uom / issue_uom too.
 */
export function resolveMedicineUomId(...sources) {
  for (const row of sources) {
    if (!row || typeof row !== 'object') continue;
    // default_issue_uom_id (item master's dispense-role UOM) wins over the
    // generic base_uom_id when both are present on the same row.
    const direct = pick(
      row, 'uom_id', 'default_issue_uom_id', 'base_uom_id', 'dispense_uom_id', 'issue_uom_id', 'sale_uom_id',
    );
    if (direct) return direct;
    const nested = row.base_uom || row.uom || row.dispense_uom || row.issue_uom;
    if (nested && typeof nested === 'object') {
      const fromNested = pick(nested, 'uom_id', 'id');
      if (fromNested) return fromNested;
    }
    const raw = row._raw;
    if (raw && raw !== row) {
      const fromRaw = resolveMedicineUomId(raw);
      if (fromRaw) return fromRaw;
    }
  }
  return '';
}

export function mapPrescribeMedicine(row) {
  const itemId = pick(row, 'item_id', 'id');
  const batches = normalizeCatalogueBatches(row);
  const priceBatch = bestPricedBatch(batches);
  const stock = Number(pick(row, 'total_available_quantity', 'available_quantity', 'stock')) || 0;
  const uomId = resolveMedicineUomId(row, priceBatch, batches[0]);
  return {
    id: itemId,
    item_id: itemId,
    item_code: pick(row, 'item_code'),
    name: pick(row, 'item_name', 'name', 'medicine_name'),
    item_name: pick(row, 'item_name', 'name', 'medicine_name'),
    generic_name: pick(row, 'generic_name'),
    brand_name: pick(row, 'brand_name'),
    dosage_form: pick(row, 'dosage_form'),
    controlled: pickBool(row, 'is_controlled_drug', 'is_narcotic', 'controlled'),
    uom_id: uomId,
    base_uom_id: uomId,
    // Issue/dispense unit as the catalogue resolved it: the unit a quantity is
    // typed in, how many base units one of them is worth, and the code to label
    // it with. A null factor means the item has no conversion path to its base
    // UOM — it cannot be dispensed until someone adds the rule.
    issue_uom_id: pick(row, 'issue_uom_id'),
    issue_uom_code: pick(row, 'issue_uom_code'),
    issue_uom_factor: pickNumOrNull(row, 'issue_uom_factor'),
    // Whole issue units the server can actually honour, summed per batch at
    // that batch's own pack size. Deliberately separate from `stock` (base
    // units): sum(floor(b/f)) and floor(sum(b)/f) are different numbers.
    total_issuable_quantity: pickNumOrNull(row, 'total_issuable_quantity'),
    base_uom_code: pick(row, 'base_uom_code', 'uom_name'),
    stock,
    available_quantity: pick(row, 'total_available_quantity', 'available_quantity') || String(stock),
    has_stock: pickBool(row, 'has_stock') || stock > 0,
    low_stock: pickBool(row, 'low_stock'),
    batch_number: pick(priceBatch || batches[0], 'batch_number'),
    expiry_date: pick(priceBatch || batches[0], 'expiry_date'),
    // Per BASE unit — kept for callers that count in base units.
    mrp: pickNum(priceBatch, 'mrp') || pickNum(row, 'mrp'),
    rate: pickNum(priceBatch, 'sale_price', 'mrp', 'unit_rate', 'rate')
      || pickNum(row, 'sale_price', 'mrp', 'unit_rate', 'rate'),
    // Per ISSUE unit — what a screen taking quantity in strips must price
    // with, so qty x rate is a real line total. Zero when the item's issue
    // UOM has no conversion, which is the signal to block the line.
    mrp_issue_uom: pickNum(priceBatch, 'mrp_issue_uom') || pickNum(row, 'mrp_issue_uom'),
    rate_issue_uom: pickNum(priceBatch, 'sale_price_issue_uom', 'mrp_issue_uom')
      || pickNum(row, 'sale_price_issue_uom', 'mrp_issue_uom'),
    // Tax Master-derived — rate/mrp above are tax-inclusive by law, so this is only
    // needed to show/report the GST already embedded in them (see splitInclusiveTax).
    gst_rate: pickNumOrNull(priceBatch, 'gst_rate') ?? pickNumOrNull(row, 'gst_rate'),
    hsn_code: pick(row, 'hsn_code'),
    batches,
    _raw: row,
  };
}

export function medicineOptionLabel(item) {
  if (!item || item.__loadMore) return item?.__loadMore ? 'Load more…' : '';
  const name = pick(item, 'item_name', 'name');
  const brand = pick(item, 'brand_name');
  const generic = pick(item, 'generic_name');
  if (brand && generic && brand !== generic) return `${name} (${brand})`;
  return name || brand || generic || '';
}

/** Exact prescribed brand / item name for prescribe/search `q`. */
export function prepareBrandSearchQuery(value) {
  return String(value || '').trim();
}

/** Generic salt name only — e.g. "Paracetamol (650mg)" → "Paracetamol". */
export function prepareGenericSearchTerm(value) {
  if (!value) return '';
  let text = String(value).trim();
  const parenIdx = text.indexOf('(');
  if (parenIdx > 0) text = text.slice(0, parenIdx);
  return text.trim();
}

/** @deprecated use prepareBrandSearchQuery or prepareGenericSearchTerm */
export function sanitizePrescribeSearchTerm(value) {
  return prepareGenericSearchTerm(value);
}

/** @deprecated use prepareGenericSearchTerm */
export const sanitizeGenericSearchTerm = prepareGenericSearchTerm;

export function countWhere(payload, predicate) {
  return toInventoryList(payload).filter(predicate).length;
}
